// Core
import PropTypes from "prop-types";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// Components
import Charts from "pages/ManageDSO/Analytics/Charts";
import KeyMetrics from "pages/ManageDSO/Analytics/KeyMetrics";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";

// Redux
import { useSelector } from "react-redux";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetDsoKpisByCommunityIdQuery,
  useGetDsoMetricsByCommunityIdQuery,
  useGetDsoMetricsMetersByCommunityIdQuery,
} from "state/dso/dsoApiSlice";
import { selectTimeSelector } from "state/application/applicationSlice";

// The DSO charts always aggregate to daily resolution — fetching at the raw
// (15-min) granularity over a wide range made this view very slow.
const DSO_CHART_GRANULARITY = "DAY";

function Analytics({ setIsLoading }) {
  const community = useSelector(selectCurrentCommunity);
  const { t } = useTranslation(["dso"]);

  const { startDate, endDate } = useSelector(selectTimeSelector);

  // These are historical aggregations (some over every meter) — refetch only
  // when the date range changes, not on a 15s poll or on every window focus,
  // which made this view hammer the backend and feel very slow.
  const fetchData = (queryFunction, additionalParams = {}) =>
    queryFunction(
      {
        communityId: community.id,
        startDate,
        endDate,
        ...additionalParams,
      },
      { refetchOnMountOrArgChange: true }
    );
  const { data: dsoKpisRaw, isLoading: isLoadingDK } = fetchData(useGetDsoKpisByCommunityIdQuery);
  const { data: dsoAggregatedRaw, isLoading: isLoadingDA } = fetchData(
    useGetDsoMetricsByCommunityIdQuery,
    { granularity: DSO_CHART_GRANULARITY }
  );
  const { data: dsoByMeterRaw, isLoading: isLoadingDM } = fetchData(
    useGetDsoMetricsMetersByCommunityIdQuery,
    { granularity: DSO_CHART_GRANULARITY }
  );

  const dsoKpis = dsoKpisRaw
    ? { ...dsoKpisRaw, total_grid_consumption: dsoKpisRaw.total_consumption }
    : dsoKpisRaw;
  const normalizeGridConsumption = (row) => ({
    ...row,
    grid_consumption: row.consumption,
  });
  const dsoAggregated = (dsoAggregatedRaw ?? []).map(normalizeGridConsumption);
  const dsoByMeter = Object.fromEntries(
    Object.entries(dsoByMeterRaw ?? {}).map(([meterId, rows]) => [
      meterId,
      rows.map(normalizeGridConsumption),
    ])
  );

  useEffect(
    () => setIsLoading(isLoadingDK || isLoadingDA || isLoadingDM),
    [isLoadingDK, isLoadingDA, isLoadingDM]
  );

  // Initial load: nothing fetched yet — show a plain text loader (no spinner).
  const isInitialLoading =
    (isLoadingDK || isLoadingDA || isLoadingDM) &&
    !dsoKpisRaw &&
    !dsoAggregatedRaw &&
    !dsoByMeterRaw;

  if (isInitialLoading) {
    return (
      <div className="flex flex-col gap-rec">
        <HelpFloatingButtonV2 helpScreen="dso-analytics" />
        <div className="rec-card rec-card-pad flex items-center justify-center py-[64px] text-ink-3 text-[13px]">
          {t("loading")}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-rec">
      <HelpFloatingButtonV2 helpScreen="dso-analytics" />
      <KeyMetrics data={dsoKpis} />
      <Charts
        dsoAggregated={dsoAggregated}
        dsoByMeter={dsoByMeter}
        granularity={DSO_CHART_GRANULARITY}
      />
    </div>
  );
}

Analytics.defaultProps = {
  setIsLoading: () => {},
};

Analytics.propTypes = {
  setIsLoading: PropTypes.func,
};

export default Analytics;
